/**
 * Lightweight SEO helper — updates <title> and key meta tags imperatively.
 * Used by individual pages (HomePage, NewsDetail) inside useEffect.
 * Avoids heavy deps like react-helmet for a single small need.
 */

const setOrCreate = (selector, attr, value, attrName = "content") => {
  if (!value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [tag] = selector.split("[");
    const m = selector.match(/\[(name|property|rel)="([^"]+)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attrName, value);
};

const setLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const DEFAULT_OG_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/d69863e8-72c5-45eb-a555-03296866412e/images/55f602fa6bd1b77132ae0dcb223658138d6d9ffd083cf083367c20b6e2932dcb.png";
const SITE_NAME = "Richild Invest GBC";
const SITE_URL = "https://richildinvest.com";

export function applySEO({
  title,
  description,
  image,
  url,
  type = "website",
  lang = "en",
  publishedTime,
}) {
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : `${SITE_NAME} — Financing Inclusive Development`;
  const finalDescription =
    description ||
    "Richild Invest GBC — International investment firm headquartered in Port Louis. Private Equity, Debt Finance, Trade Finance, Advisory, Impact Investing across 12 offices worldwide.";
  const finalImage = image || DEFAULT_OG_IMAGE;
  const finalUrl = url || SITE_URL;

  document.title = fullTitle;
  document.documentElement.setAttribute("lang", lang);

  // Standard meta
  setOrCreate('meta[name="description"]', "content", finalDescription);
  setLink("canonical", finalUrl);

  // Open Graph
  setOrCreate('meta[property="og:title"]', "content", fullTitle);
  setOrCreate('meta[property="og:description"]', "content", finalDescription);
  setOrCreate('meta[property="og:image"]', "content", finalImage);
  setOrCreate('meta[property="og:url"]', "content", finalUrl);
  setOrCreate('meta[property="og:type"]', "content", type);
  setOrCreate('meta[property="og:site_name"]', "content", SITE_NAME);
  setOrCreate('meta[property="og:locale"]', "content", lang === "fr" ? "fr_FR" : "en_US");
  if (publishedTime) {
    setOrCreate('meta[property="article:published_time"]', "content", publishedTime);
  }

  // Twitter card
  setOrCreate('meta[name="twitter:card"]', "content", "summary_large_image");
  setOrCreate('meta[name="twitter:title"]', "content", fullTitle);
  setOrCreate('meta[name="twitter:description"]', "content", finalDescription);
  setOrCreate('meta[name="twitter:image"]', "content", finalImage);
}
